<template>
  <div>
    <TitleWrapper
      :filter-option="false"
      title="ANNOUNCEMENT_DETAIL"
      tooltip-title="ANNOUNCEMENT_DETAIL"
      :display-breadcrumb="true"
    >
      <template v-slot:filterItems>
        <h2 v-i18n="result" class="mb-3 text-text-color font-roboto font-normal text-xl">Search</h2>
      </template>
    </TitleWrapper>
    <div
      class="flex flex-col p-5 border border-primary-gray-200 bg-white rounded-lg text-base capitalize md:mt-5"
    >
      <div class="py-3 flex text-sm overflow-hidden">
        <span class="font-medium text-primary-gray-700 min-w-[160px]">Title:</span>
        <span class="font-normal text-primary-gray-600">
          {{ announcementData.title || GENERAL_CONSTANTS.NOT_APPLICABLE }}
        </span>
      </div>
      <div class="py-3 flex text-sm overflow-hidden">
        <span class="font-medium text-primary-gray-700 min-w-[160px]">Announcement Type:</span>
        <span class="font-normal text-primary-gray-600">
          {{ announcementData?.announcement_type?.title || GENERAL_CONSTANTS.NOT_APPLICABLE }}
        </span>
      </div>
      <div class="py-3 flex text-sm overflow-hidden">
        <span class="font-medium text-primary-gray-700 min-w-[160px]">Role:</span>
        <span class="font-normal text-primary-gray-600">
          {{ announcementData.role || GENERAL_CONSTANTS.NOT_APPLICABLE }}
        </span>
      </div>
      <div class="py-3 flex text-sm overflow-hidden">
        <span class="font-medium text-primary-gray-700 min-w-[160px]">Date:</span>
        <span class="font-normal text-primary-gray-600">
          {{
            $filters.dateFormatInUTC(announcementData.inserted_at, TIME_CONSTANTS.DATE) ||
            GENERAL_CONSTANTS.NOT_APPLICABLE
          }}
        </span>
      </div>
      <div v-if="announcementData.email_body" class="py-3 pb-6 flex text-sm overflow-hidden">
        <span class="font-medium text-primary-gray-700 min-w-[160px]">Email Body:</span>
        <div
          class="font-normal text-primary-gray-600 line-clamp-2 xl:line-clamp-3 xxl:line-clamp-none cursor-pointer"
          @click="modalOpen('Email', announcementData.email_body)"
        >
          {{ announcementData.email_body }}
        </div>
      </div>
      <div v-if="announcementData.webpush_body" class="py-3 pb-6 flex text-sm overflow-hidden">
        <span class="font-medium text-primary-gray-700 min-w-[160px]">in-app notification:</span>
        <div
          class="font-normal text-primary-gray-600 line-clamp-2 xl:line-clamp-3 xxl:line-clamp-none cursor-pointer"
          @click="modalOpen('in-app notification', announcementData.webpush_body)"
        >
          {{ announcementData.webpush_body }}
        </div>
      </div>
      <div v-if="announcementData.message_body" class="py-3 pb-6 flex text-sm overflow-hidden">
        <span class="font-medium text-primary-gray-700 min-w-[160px]">sms Body:</span>
        <div
          class="font-normal text-primary-gray-600 line-clamp-2 xl:line-clamp-3 xxl:line-clamp-none cursor-pointer"
          @click="modalOpen('sms', announcementData.message_body)"
        >
          {{ announcementData.message_body }}
        </div>
      </div>
    </div>
    <div v-if="isLoading" class="mt-28"><Loader :content="true" /></div>
    <div v-else-if="!isLoading && isEmpty(notificationUserList)">
      <NoRecordFound />
    </div>

    <div v-else class="md:mx-0 mx-5">
      <Heading heading="Receiver List" />
      <TableWrapper>
        <THead>
          <TRHead>
            <TH v-for="(head, index) in tableHeaders" :key="index">
              {{ head }}
            </TH>
          </TRHead>
        </THead>

        <TBody>
          <TRBody v-for="(notification, index) in notificationUserList" :key="index">
            <TD>
              <SingleUserDisplay :user="notification.receiver" label="full_name" :image="true" />
            </TD>

            <TD :lowercase="true">
              {{ notification.receiver.email || GENERAL_CONSTANTS.NOT_APPLICABLE }}
            </TD>

            <TD>
              {{ notification.receiver.phone || GENERAL_CONSTANTS.NOT_APPLICABLE }}
            </TD>
            <TD>
              <EmailStatus :key="index" :notification="notification" />
            </TD>
            <TD>
              <div
                class="px-3 py-2 rounded-md text-center"
                :style="getMediumStyle(notification.sms)"
              >
                {{
                  (notification.sms && notification.sms.status) || GENERAL_CONSTANTS.NOT_APPLICABLE
                }}
              </div>
            </TD>
            <TD>
              <WebPushStatus
                :key="index"
                :notification="notification"
                :tooltip-position="index === notificationUserList.length - 1"
              />
            </TD>
          </TRBody>
        </TBody>
      </TableWrapper>
    </div>
    <Pagination
      v-if="totalNotificationCount > GENERAL_CONSTANTS.RECORD_LIMIT"
      v-show="!isLoading"
      :key="`ForceRender${forceRender}`"
      :record-limit="filteredRecordLimit"
      :max-range="paginationCounts(totalNotificationCount, filteredRecordLimit)"
      @filterRecord="_getAnnouncements"
    />
    <SingleNotificationModal></SingleNotificationModal>
    <UITextDetailModal
      :heading="modalHeading"
      :details="modalDetail"
      :modal="showTextDetailModal"
      @cancel="modalClose"
    />
  </div>
</template>

<script>
import isEmpty from 'lodash/isEmpty'
import Loader from '@components/BaseComponent/Loader.vue'
import TitleWrapper from '@components/TitleWrapper.vue'
import { mapActions, mapState } from 'vuex'
import generalMixin from '@src/mixins/general-mixins.js'
import { transformTextToCapitalize } from '@utils/generalUtil'
import SingleNotificationModal from '@/src/router/views/admin/modals/AnnouncementUpsertModal.vue'
import Pagination from '@components/BaseComponent/Pagination.vue'
import { getTimeFromDateWithUTC } from '@utils/moment.util'
import TIME_CONSTANTS from '@src/constants/date-time-constants'
import GENERAL_CONSTANTS, { COMMUNICATION_MEDIUMS } from '@src/constants/general-constants'
import scrollMixin from '@src/mixins/scroll-mixin'
import NoRecordFound from '@/src/components/BaseComponent/NoRecordFound.vue'
import SingleUserDisplay from '@src/components/UiElements/SingleUserDisplay.vue'
import Heading from '@src/components/Heading.vue'
import EmailStatus from '@src/router/views/notifications/DetailView/Email.vue'
import WebPushStatus from '@src/router/views/notifications/DetailView/WebPush.vue'
import { getMediumStyle } from '@src/utils/filters/announcements/index.js'
import {
  TableWrapper,
  TBody,
  TD,
  TH,
  THead,
  TRBody,
  TRHead,
} from '@src/components/UiElements/TableElements/index.js'
import UITextDetailModal from '@/src/components/UiElements/UiTextDetailModal.vue'

export default {
  components: {
    SingleUserDisplay,
    Loader,
    TitleWrapper,
    SingleNotificationModal,
    Pagination,
    NoRecordFound,
    EmailStatus,
    WebPushStatus,
    Heading,
    TableWrapper,
    TBody,
    TD,
    TH,
    THead,
    TRBody,
    TRHead,
    UITextDetailModal,
  },
  mixins: [generalMixin, scrollMixin],
  data() {
    return {
      showTextDetailModal: false,
      modalHeading: 'heading',
      modalDetail: 'detail',
      TIME_CONSTANTS,
      forceRender: 0,
      result: 'result',
      title: 'title',
      dashboard: 'dashboard',
      totalNotificationCount: 0,
      announcementId: this.$route.params.announcementId,
      options: ['Select'],

      tableHeaders: [
        'User Name',
        'Email',
        'Phone',
        'Email Status',
        'Phone Status',
        'Web Push Status',
      ],
      announcementData: {},
      notificationUserList: [],
      isLoading: false,
      noRecord: false,
      filteredRecordLimit: 10,
      GENERAL_CONSTANTS,
      actionList: [{ name: 'SNA_RET' }, { name: 'SNA_RES' }, { name: 'SNA_DUP' }],
      isEmpty,
    }
  },
  computed: {
    ...mapState({
      showModal: (state) => state.layout.showModal,
    }),
  },
  created() {
    this._getAnnouncements()
  },
  methods: {
    getMediumStyle,
    modalOpen(heading, detail) {
      this.modalHeading = heading
      this.modalDetail = detail
      this.showTextDetailModal = true
    },
    modalClose() {
      this.showTextDetailModal = false
    },
    async _getAnnouncements(range) {
      const payload = {
        range: {
          skip: range?.skip || this.GENERAL_CONSTANTS.RECORD_SKIP,
          limit: range?.limit || this.GENERAL_CONSTANTS.RECORD_LIMIT,
        },
      }
      payload.announcementId = this.announcementId
      this.isLoading = true
      const [res, err] = await this.getAnnouncemntById(payload)
      this.isLoading = false
      if (err) return
      this.announcementData = res.data.announcement_data
      this.announcementData.role = transformTextToCapitalize(this.announcementData.receiver_role_id)
      this.totalNotificationCount = res.data.meta.total_records
      this.notificationUserList = res.data.records.map((rec) => {
        rec.receiver.full_name = `${rec.receiver.first_name} ${rec.receiver.last_name}`
        rec.receiver_mediums_data.forEach((med) => {
          if (med.medium === COMMUNICATION_MEDIUMS.WEBPUSH) {
            rec.webpush = med
          }
          if (med.medium === COMMUNICATION_MEDIUMS.SMS) {
            rec.sms = med
          }
          if (med.medium === COMMUNICATION_MEDIUMS.EMAIL) {
            rec.email = med
          }
        })
        return rec
      })
    },

    ...mapActions('notifications', ['getAnnouncemntById']),
    ...mapActions('layout', ['setRightbarContent', 'setRightBarStats', 'setShowModal']),
  },
}
</script>
